import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from '../../store/useStore';
import { predraw, postdraw } from '../Canvas/canvasUtils';
import { ResponsiveCanvas } from '../Canvas/ResponsiveCanvas.component';
import { ThemeContext } from 'styled-components';
import { ThemeInterface } from '../../interfaces/themeInterface';
import {
  buildMarkovTransitionForecast,
  MarkovTransitionForecast,
  MarkovTransitionPrediction,
} from '../../services/markovEvolution';
import * as S from './WolframCA.style';

interface WolframContext {
  ruleset: (number | undefined)[];
  theme: ThemeInterface | undefined;
}

const DISPLAY_TOP_K = 3;
const EMA_WEIGHT = 0.24;
const LEADER_SWITCH_THRESHOLD = 0.04;

const normalizePredictions = (predictions: MarkovTransitionPrediction[]) => {
  const total = predictions.reduce((sum, prediction) => sum + prediction.probability, 0);
  if (total <= 0) return predictions;
  return predictions.map((prediction) => ({
    ...prediction,
    probability: prediction.probability / total,
  }));
};

const smoothForecast = (
  previous: MarkovTransitionForecast | null,
  incoming: MarkovTransitionForecast,
) => {
  if (!previous || previous.predictions.length === 0) {
    return {
      ...incoming,
      predictions: incoming.predictions.slice(0, DISPLAY_TOP_K),
    };
  }

  const previousMap = new Map(previous.predictions.map((prediction) => [prediction.id, prediction]));
  const incomingMap = new Map(incoming.predictions.map((prediction) => [prediction.id, prediction]));
  const ids = new Set([...incomingMap.keys(), ...previousMap.keys()]);

  const merged = Array.from(ids).map((id) => {
    const incomingPrediction = incomingMap.get(id);
    const previousPrediction = previousMap.get(id);
    const nextProbability = incomingPrediction?.probability ?? 0;
    const prevProbability = previousPrediction?.probability ?? 0;

    return {
      id,
      label: incomingPrediction?.label ?? previousPrediction?.label ?? id,
      count: incomingPrediction?.count ?? 0,
      probability: prevProbability * (1 - EMA_WEIGHT) + nextProbability * EMA_WEIGHT,
    };
  });

  const ranked = normalizePredictions(merged).sort((a, b) => b.probability - a.probability);

  const previousLeaderId = previous.predictions[0]?.id;
  const incomingLeader = ranked[0];
  const previousLeaderIndex = ranked.findIndex((prediction) => prediction.id === previousLeaderId);

  if (
    previousLeaderId &&
    incomingLeader &&
    previousLeaderIndex > 0 &&
    incomingLeader.id !== previousLeaderId
  ) {
    const previousLeader = ranked[previousLeaderIndex];
    if (incomingLeader.probability - previousLeader.probability < LEADER_SWITCH_THRESHOLD) {
      ranked.splice(previousLeaderIndex, 1);
      ranked.unshift(previousLeader);
    }
  }

  return {
    ...incoming,
    predictions: ranked.slice(0, DISPLAY_TOP_K),
  };
};

export const WolframCA = () => {
  const { lastByteActivity } = useStore((state) => state.dataFeed);

  const theme = useContext(ThemeContext);

  const getRule = (byteArr: string[] = []): (number | undefined)[] => {
    const map: Record<string, number> = {
      '0+': 1,
      '0-': 0,
      '+': 1,
      '-': 0,
    };
    return byteArr.map((b) => map[b]);
  };

  const ruleset = getRule(lastByteActivity);
  const normalizedRuleset = ruleset.slice(0, 8).map((value) => (value === 1 ? 1 : 0));
  const ruleSignature = normalizedRuleset.join('');

  const forecast = useMemo(
    () =>
      buildMarkovTransitionForecast(normalizedRuleset, {
        cells: 84,
        generations: 128,
        topK: 6,
        horizon: 4,
        immediateWeight: 0.35,
      }),
    [ruleSignature],
  );
  const [stableForecast, setStableForecast] = useState<MarkovTransitionForecast | null>(forecast);

  useEffect(() => {
    if (!forecast) {
      setStableForecast(null);
      return;
    }

    setStableForecast((previous) => smoothForecast(previous, forecast));
  }, [forecast]);

  const canvasOptions = {
    context: {
      ruleset,
      theme,
    },
    predraw,
    postdraw,
  };

  const draw = (ctx: CanvasRenderingContext2D, width: number, height: number, context: unknown) => {
    const { ruleset, theme } = context as WolframContext;
    if (!theme || ruleset.length < 8) return;

    const currentTheme = theme;

    const internalState = {
      w: 10,
      cells: [] as number[],
      generation: 0,
      ruleset,
    };

    const rules = (a: number, b: number, c: number, ruleset: (number | undefined)[]) => {
      if (a == 1 && b == 1 && c == 1) return ruleset[0];
      if (a == 1 && b == 1 && c == 0) return ruleset[1];
      if (a == 1 && b == 0 && c == 1) return ruleset[2];
      if (a == 1 && b == 0 && c == 0) return ruleset[3];
      if (a == 0 && b == 1 && c == 1) return ruleset[4];
      if (a == 0 && b == 1 && c == 0) return ruleset[5];
      if (a == 0 && b == 0 && c == 1) return ruleset[6];
      if (a == 0 && b == 0 && c == 0) return ruleset[7];

      return 0;
    };

    const init = () => {
      const newCells = Array(Math.floor(width / internalState.w)).fill(0);
      newCells[newCells.length / 2] = 1;
      internalState.cells = newCells;
    };

    function _draw() {
      internalState.cells.forEach((cell, i) => {
        if (cell === 1) ctx.fillStyle = currentTheme.primary;
        else {
          ctx.fillStyle = currentTheme.primary;
          ctx.fillRect(
            i * internalState.w,
            internalState.generation * internalState.w,
            internalState.w,
            internalState.w,
          );
        }
      });

      if (internalState.generation < height / internalState.w) _generate();
    }

    function _generate() {
      const nextgen = Array(internalState.cells.length) as number[];

      for (let i = 1; i < internalState.cells.length - 1; i++) {
        const left = internalState.cells[i - 1];
        const me = internalState.cells[i];
        const right = internalState.cells[i + 1];
        nextgen[i] = rules(left, me, right, internalState.ruleset) ?? 0;
      }

      internalState.cells = nextgen;
      internalState.generation++;

      _draw();
    }

    init();
    _draw();
  };

  return (
    <S.FieldWrapper>
      <S.CanvasRegion>
        <ResponsiveCanvas draw={draw} options={canvasOptions} />
      </S.CanvasRegion>

      <S.ForecastCard>
        <S.ForecastHead>
          <S.ForecastKicker>markov next transitions</S.ForecastKicker>
          <S.ForecastMeta>
            {stableForecast ? `${stableForecast.sampleSize} samples` : 'awaiting samples'}
          </S.ForecastMeta>
        </S.ForecastHead>

        {stableForecast ? (
          <>
            <S.CurrentState>current: {stableForecast.currentStateLabel}</S.CurrentState>
            {stableForecast.predictions.length > 0 ? (
              <S.ForecastList>
                {stableForecast.predictions.map((prediction) => (
                  <S.ForecastItem key={prediction.id}>
                    <S.ForecastLabel>{prediction.label}</S.ForecastLabel>
                    <S.ForecastProb>{Math.round(prediction.probability * 100)}%</S.ForecastProb>
                    <S.ForecastTrack>
                      <S.ForecastFill $ratio={prediction.probability} />
                    </S.ForecastTrack>
                  </S.ForecastItem>
                ))}
              </S.ForecastList>
            ) : (
              <S.EmptyState>not enough transitions yet to estimate next states.</S.EmptyState>
            )}
          </>
        ) : (
          <S.EmptyState>awaiting complete rule set to initialize markov estimator.</S.EmptyState>
        )}
      </S.ForecastCard>
    </S.FieldWrapper>
  );
};
